import React, { useState, useEffect } from "react";
import Modal from "../Modal/Modal";
import InputField from "../input-field/InputField";
import ArrowLeftWithBody from "../../assets/icons/ArrowLeftWithBody";
import CheckMark from "../../assets/icons/CheckMark";
import { showSnackbar } from "../../misc/utils";
import { simpleGetter } from "../../actions/common";
import { lessonsApi, newUniversalApi, subjectsApi } from "../../api/api";
import { useRooms } from "../../hooks/cabinet";
import { useUsersWithSocialScoreAndDemerits } from "../../hooks/demerit-typed";
import DatePicker from "react-datepicker";
import { CustomDateInput } from "../CustomDateInput";

interface MainUserWithRoles {
  id: number;
  json_saved_data?: { firstname?: string; lastname?: string; email?: string };
  roles?: { name: string }[];
}

interface Course {
  id: number;
  name: string;
  main_user_id?: number;
}

interface AddSubjectLessonModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  onSuccess?: () => void;
}

const timeSlots = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30",
];

const TEACHER_ROLES = ["TEACHER", "ADMIN", "SUPERADMIN", "MODERATOR"];

const NEW_SUBJECT_VALUE = "__new__";
const SUBJECT_PREFIX = "subject-";
const COURSE_PREFIX = "course-";

export default function AddSubjectLessonModal({
  visible,
  setVisible,
  onSuccess,
}: AddSubjectLessonModalProps) {
  const [teacher, setTeacher] = useState<MainUserWithRoles | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const [newSubjectName, setNewSubjectName] = useState("");
  const [roomId, setRoomId] = useState<string>("");
  const [date, setDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [catalogSubjects, setCatalogSubjects] = useState<{ id: number; name: string }[]>([]);

  const rooms = useRooms("", visible ? 1 : 0, false);
  const [teachersList] = useUsersWithSocialScoreAndDemerits<"roles">(
    visible ? 1 : 0,
    { page: 0, type: "roles", usersWhere: {} }
  );

  const teachers = (teachersList || []).filter((u: MainUserWithRoles) =>
    u.roles?.some((r) => TEACHER_ROLES.includes(r.name))
  );

  const isNewSubject = selectedCourseId === NEW_SUBJECT_VALUE;
  const isSubjectFromCatalog = selectedCourseId.startsWith(SUBJECT_PREFIX);
  const isCourseSelected = selectedCourseId.startsWith(COURSE_PREFIX);
  const subjectName = isNewSubject
    ? newSubjectName.trim()
    : isSubjectFromCatalog
      ? catalogSubjects.find((s) => `${SUBJECT_PREFIX}${s.id}` === selectedCourseId)?.name ?? ""
      : isCourseSelected
        ? courses.find((c) => `${COURSE_PREFIX}${c.id}` === selectedCourseId)?.name ?? ""
        : "";

  useEffect(() => {
    if (!visible) {
      setTeacher(null);
      setSelectedCourseId("");
      setNewSubjectName("");
      setRoomId("");
      setDate(null);
      setStartTime("");
      setEndTime("");
      setCourses([]);
      setCatalogSubjects([]);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const fetchCatalogSubjects = async () => {
      const res = await simpleGetter(subjectsApi.findAll, {});
      const list = (res?.list ?? []).map((s: { id: number; name: string }) => ({ id: s.id, name: s.name }));
      setCatalogSubjects(list);
    };
    fetchCatalogSubjects();
  }, [visible]);

  useEffect(() => {
    if (!visible || !teacher) {
      setCourses([]);
      return;
    }
    const fetchCourses = async () => {
      const res = await simpleGetter(newUniversalApi.find, {
        model: "Course",
        body: {
          filters: { main_user_id: teacher.id },
          user_params: { skip_user_params: true },
        },
      });
      if (res.list) {
        setCourses(res.list);
        setSelectedCourseId("");
      }
    };
    fetchCourses();
  }, [visible, teacher?.id]);

  const getDisplayCourses = () =>
    courses.map((c) => ({ id: `${COURSE_PREFIX}${c.id}`, name: c.name }));
  const getDisplayCatalogSubjects = () =>
    catalogSubjects.filter(
      (s) => !courses.some((c) => c.name === s.name)
    ).map((s) => ({ id: `${SUBJECT_PREFIX}${s.id}`, name: s.name }));

  const getTeacherName = (u: MainUserWithRoles) => {
    const j = u.json_saved_data || {};
    return `${j.lastname || ""} ${j.firstname || ""}`.trim() || `ID ${u.id}`;
  };

  const isFormValid =
    teacher &&
    (selectedCourseId === NEW_SUBJECT_VALUE ? newSubjectName.trim() : selectedCourseId) &&
    roomId &&
    date &&
    startTime &&
    endTime &&
    startTime < endTime;

  const handleSubmit = async () => {
    if (!isFormValid || !teacher) return;
    setIsSubmitting(true);
    try {
      let courseId: number;
      let classId: number;
      let title: string;

      if (isNewSubject || isSubjectFromCatalog) {
        const courseName = isNewSubject ? newSubjectName.trim() : subjectName;
        const courseRes = await simpleGetter(newUniversalApi.create, {
          model: "Course",
          body: {
            create: {
              name: courseName,
              main_user_id: teacher.id,
            },
            user_params: { skip_user_params: true },
          },
        });
        if (!courseRes.item?.id) {
          showSnackbar("Ошибка при создании предмета", "error");
          return;
        }
        courseId = courseRes.item.id;
        title = courseName;

        const classRes = await simpleGetter(newUniversalApi.create, {
          model: "Class",
          body: {
            create: {
              course_id: courseId,
              name: `${title} - Группа 1`,
            },
            user_params: { skip_user_params: true },
          },
        });
        if (!classRes.item?.id) {
          showSnackbar("Ошибка при создании класса", "error");
          return;
        }
        classId = classRes.item.id;
      } else {
        courseId = parseInt(selectedCourseId.replace(COURSE_PREFIX, ""), 10);
        const classRes = await simpleGetter(newUniversalApi.find, {
          model: "Class",
          body: {
            filters: { course_id: courseId },
            user_params: { skip_user_params: true },
          },
        });
        const classes = classRes.list || [];
        if (classes.length === 0) {
          const createClassRes = await simpleGetter(newUniversalApi.create, {
            model: "Class",
            body: {
              create: {
                course_id: courseId,
                name: `${subjectName} - Группа 1`,
              },
              user_params: { skip_user_params: true },
            },
          });
          if (!createClassRes.item?.id) {
            showSnackbar("Ошибка при создании класса", "error");
            return;
          }
          classId = createClassRes.item.id;
        } else {
          classId = classes[0].id;
        }
        title = subjectName;
      }

      // 3. Создать урок
      const startDateTime = new Date(
        `${date!.toDateString()} ${startTime}`
      );
      const endDateTime = new Date(
        `${date!.toDateString()} ${endTime}`
      );

      const lessonRes = await simpleGetter(lessonsApi.create, {
        main_user_id: teacher.id,
        class_id: classId,
        room_id: parseInt(roomId, 10),
        title,
        description: "",
        start_time: startDateTime,
        end_time: endDateTime,
        is_attendance_ongoing: 0,
      });

      if (lessonRes.success) {
        showSnackbar("Предмет и урок успешно добавлены преподавателю", "success");
        setVisible(false);
        onSuccess?.();
      } else {
        showSnackbar("Ошибка при создании урока", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Произошла ошибка", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      additionalClassName="sm:w-4/5 w-3/5 max-w-2xl"
    >
      <div className="flex flex-col p-6 md:p-8 bg-white text-[#1A1A1A]">
        <h2 className="text-2xl text-[#1A1A1A] mb-6 font-semibold">
          Добавить предмет и урок преподавателю
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#5F6B88] mb-2">
              Преподаватель
            </label>
            <select
              className="w-full border rounded-lg p-2.5 text-sm text-[#1A1A1A] focus:ring-2 focus:ring-valley-main bg-[#F0F3FA] border-gray-200"
              value={teacher?.id ?? ""}
              onChange={(e) => {
                const id = parseInt(e.target.value);
                setTeacher(teachers.find((t) => t.id === id) || null);
              }}
            >
              <option value="">Выберите преподавателя</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {getTeacherName(t)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#5F6B88] mb-2">
              Предмет
            </label>
            <select
              className="w-full border rounded-lg p-2.5 text-sm text-[#1A1A1A] focus:ring-2 focus:ring-valley-main bg-[#F0F3FA] border-gray-200"
              value={selectedCourseId}
              onChange={(e) => setSelectedCourseId(e.target.value)}
              disabled={!teacher}
            >
              <option value="">Выберите предмет</option>
              {getDisplayCourses().map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              {getDisplayCatalogSubjects().length > 0 && (
                <optgroup label="Из справочника предметов">
                  {getDisplayCatalogSubjects().map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </optgroup>
              )}
              <option value={NEW_SUBJECT_VALUE}>+ Создать новый предмет</option>
            </select>
            {isNewSubject && (
              <div className="mt-2">
                <InputField
                  type="text"
                  id="newSubjectName"
                  label="Название нового предмета"
                  value={newSubjectName}
                  handleChange={(e) => setNewSubjectName(e.target.value)}
                  placeholder="Введите название предмета"
                  additionalClassName="admin-input placeholder-valley-light-gray text-[#1A1A1A]"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#5F6B88] mb-2">
              Кабинет
            </label>
            <select
              className="w-full border rounded-lg p-2.5 text-sm text-[#1A1A1A] focus:ring-2 focus:ring-valley-main bg-[#F0F3FA] border-gray-200"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
            >
              <option value="">Выберите кабинет</option>
              {rooms.map((r: { id: number; name: string }) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#5F6B88] mb-2">
              Дата
            </label>
            <DatePicker
              selected={date}
              onChange={(d) => setDate(d)}
              customInput={<CustomDateInput hasTime={false} />}
              dateFormat="dd/MM/yyyy"
              className="w-full text-[#1A1A1A]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#5F6B88] mb-2">
                Время начала
              </label>
              <select
                className="w-full border rounded-lg p-2.5 text-sm text-[#1A1A1A] focus:ring-2 focus:ring-valley-main bg-[#F0F3FA] border-gray-200"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              >
                <option value="">--:--</option>
                {timeSlots.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#5F6B88] mb-2">
                Время окончания
              </label>
              <select
                className="w-full border rounded-lg p-2.5 text-sm text-[#1A1A1A] focus:ring-2 focus:ring-valley-main bg-[#F0F3FA] border-gray-200"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              >
                <option value="">--:--</option>
                {timeSlots.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <button
            className="w-full sm:w-auto flex-1 rounded-lg flex items-center justify-center bg-valley-gray py-3 text-white transition hover:opacity-90"
            onClick={() => setVisible(false)}
          >
            <ArrowLeftWithBody additionalStyle="text-white" />
            <span className="ml-2 font-bold text-[15px]">Вернуться</span>
          </button>
          <button
            className={`w-full sm:w-auto flex-1 rounded-lg flex items-center justify-center bg-valley-main py-3 text-white transition ${
              !isFormValid || isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
            disabled={!isFormValid || isSubmitting}
            onClick={handleSubmit}
          >
            <CheckMark additionalStyle="text-white" />
            <span className="ml-2 font-bold text-[15px]">
              {isSubmitting ? "Создание..." : "Добавить"}
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
}
